const { getContracts, getNetworkId } = require('./utils.js');
const constants = require('./constants.js');
const { ContainerType, ByteVectorType } = require("@chainsafe/ssz");

const BLS = require('@chainsafe/bls/herumi');
const { decrypt } = require('@chainsafe/bls-keystore');

const _ = require('lodash');
const { remove0x, add0x} = require("./utils");
const axios = require('axios');

const Registration = new ContainerType({
    fields: {
        blsKey: new ByteVectorType({ length: 48 }),
        payoutRecipient: new ByteVectorType({ length: 20 }),
        domain: new ByteVectorType({ length: 20 }),
    }
});

const _getRegistry = async (signer) => (await getContracts(signer)).proposerRegistry;

const _formRegistrationSignature = async (
    blsKey, payoutRecipient, proposerRegistryAddress, blsSigningKey
) => {

    if (!_.isString(blsKey)) {
        throw Error('BLS public key must be provided in string format')
    }

    if (!_.isString(payoutRecipient)) {
        throw Error('Payout recipient must be provided in string format')
    }

    if (!_.isString(proposerRegistryAddress)) {
        throw Error('Proposer registry must be provided in string format');
    }

    const registration = Registration.fromJson({
        blsKey: remove0x(blsKey),
        payoutRecipient: remove0x(payoutRecipient),
        domain: remove0x(proposerRegistryAddress)
    });

    const root = Registration.hashTreeRoot(registration);

    await BLS.init();
    const { bls } = BLS;

    return bls.sign(blsSigningKey, root);
};

const _swapSignature = async (signer, signature, blsKey, payoutRecipient, representative) => {
    const chainId = await getNetworkId(signer);
    const network = constants.chainIdToNetworkmap[chainId];

    if (!network) {
        throw new Error('Invalid network');
    }

    const swapperUrl = constants.SIGNATURE_SWAPPER[network];

    let res;
    try {
        res = await axios.post(swapperUrl, {
            signature,
            blsKey,
            payoutRecipient,
            representativeAddress: representative
        });
    }
    catch (error) {
        throw new Error(error.response.data.reason);
    }

    return res.data;
}

const _prepareRegistration = async (signer, blsKey, payoutRecipient, representative, blsSigningKey) => {
    const networkId = await getNetworkId(signer);
    const networkName = constants.chainIdToNetworkmap[networkId];
    const proposerRegistryAddress = constants.CONTRACT_ADDRESSES[networkName].PROPOSER_REGISTRY;

    const registrationSignature = await _formRegistrationSignature(
        blsKey,
        payoutRecipient,
        proposerRegistryAddress,
        blsSigningKey
    );

    const ecdsaSig = await _swapSignature(
        signer,
        Buffer.from(registrationSignature).toString('hex'),
        blsKey,
        payoutRecipient,
        representative
    );

    return {
        registrationSignature,
        ecdsaSig,
        blsPublicKey: blsKey,
        payoutRecipient,
        representative
    }
}

const _registerProposer = async (signer, blsKey, payoutRecipient, representative, blsSigningKey, openClaim) => {
    const {
        registrationSignature,
        ecdsaSig
    } = await _prepareRegistration(signer, blsKey, payoutRecipient, representative, blsSigningKey);

    const registry = await _getRegistry(signer);
    return registry.registerProposer(
        Buffer.from(add0x(blsKey), 'hex'),
        payoutRecipient,
        registrationSignature,
        ecdsaSig,
        openClaim
    );
};

const _registerProposerWithSignature = async (signer, blsKey, payoutRecipient, registrationSignature, ecdsaSig, openClaim) => {
    const registry = await _getRegistry(signer);
    return registry.registerProposer(
        Buffer.from(add0x(blsKey), 'hex'),
        payoutRecipient,
        registrationSignature,
        ecdsaSig,
        openClaim
    );
}

const _batchRegisterProposer = async (signer, blsKeys, payoutRecipients, representatives, blsSigningKeys, openClaims) => {
    let preparationPromises = []
    for (let i = 0; i < blsKeys.length; ++i) {
        preparationPromises.push(_prepareRegistration(signer, blsKeys[i], payoutRecipients[i], representatives[i], blsSigningKeys[i]))
    }

    const results = await Promise.all(preparationPromises);
    let blsSignatures = []
    let ecdsaSignatures = []
    for (let i = 0; i < blsKeys.length; ++i) {
        const {
            registrationSignature,
            ecdsaSig
        } = results[i]

        ecdsaSignatures.push(ecdsaSig);
        blsSignatures.push(registrationSignature);
    }

    const registry = await _getRegistry(signer);
    return registry.batchRegisterProposer(
        blsKeys,
        payoutRecipients,
        blsSignatures,
        ecdsaSignatures,
        openClaims
    )
}

const _registerProposerWithoutPayoutPoolRegistration = async (signer, blsKey, alternativeFeeRecipient, payoutRecipient, representative, blsSigningKey, openClaim) => {
    const {
        registrationSignature,
        ecdsaSig
    } = await _prepareRegistration(signer, blsKey, payoutRecipient, representative, blsSigningKey);

    const registry = await _getRegistry(signer);
    return registry.registerProposerWithoutPayoutPoolRegistration(
        Buffer.from(add0x(blsKey), 'hex'),
        payoutRecipient,
        registrationSignature,
        ecdsaSig,
        openClaim,
        alternativeFeeRecipient
    );
}

const _batchRegisterProposerWithoutPayoutPoolRegistration = async (signer, blsKeys, alternativeFeeRecipients, payoutRecipients, representatives, blsSigningKeys, openClaims) => {
    let preparationPromises = []
    for (let i = 0; i < blsKeys.length; ++i) {
        preparationPromises.push(_prepareRegistration(signer, blsKeys[i], payoutRecipients[i], representatives[i], blsSigningKeys[i]))
    }

    const results = await Promise.all(preparationPromises);
    let blsSignatures = []
    let ecdsaSignatures = []
    for (let i = 0; i < blsKeys.length; ++i) {
        const {
            registrationSignature,
            ecdsaSig
        } = results[i];

        ecdsaSignatures.push(ecdsaSig);
        blsSignatures.push(registrationSignature);
    }

    const registry = await _getRegistry(signer);
    return registry.batchRegisterProposerWithoutPayoutPoolRegistration(
        blsKeys,
        payoutRecipients,
        blsSignatures,
        ecdsaSignatures,
        openClaims,
        alternativeFeeRecipients
    );
}

const _activateProposer = async (signer) => {
    const registry = await _getRegistry(signer);

    return registry.activateProposers();
};

const _getProposer = async (signer, blsPublicKey) => {
    const registry = await _getRegistry(signer);

    const proposer = await registry.blsPublicKeyToProposer(blsPublicKey);
    const [joinBlock, activationBlock, exitBlock, owner, initializer, status, reportCount] = proposer;

    return {
        joinBlock: joinBlock.toString(),
        activationBlock: activationBlock.toString(),
        exitBlock: exitBlock.toString(),
        owner,
        initializer,
        status,
        reportCount,
    }
}

const _positionProposerForRagequit = async (signer, blsPublicKey) => {
    const registry = await _getRegistry(signer);

    return registry.positionForRagequit(blsPublicKey);
}

const _ragequitProposer = async (signer, blsPublicKey) => {
    const registry = await _getRegistry(signer);

    return registry.ragequitProposer(blsPublicKey);
}

/// @notice Utility function for unlocking an Ethereum BLS keystore file for use in signing messages
const _unlockBLSKeystore = async (keystore, password) => {
    await BLS.init();
    const { bls } = BLS;

    const decryptedSigningKey = await decrypt(keystore, password);

    let publicKey = bls.secretKeyToPublicKey(decryptedSigningKey);

    publicKey = Buffer.from(publicKey).toString('hex');
    const privateKey = Buffer.from(decryptedSigningKey).toString('hex');

    return { publicKey, privateKey };
};

class ProposerRegistry {

    constructor(signer) {
        this.signer = signer;
    }

    getRegistry() {
        return _getRegistry(this.signer);
    }

    unlockBLSKeystore(keystore, password) {
        return _unlockBLSKeystore(keystore, password);
    }

    prepareRegistration(blsKey, payoutRecipient, representative, blsSigningKey) {
        return _prepareRegistration(this.signer, blsKey, payoutRecipient, representative, blsSigningKey);
    }

    /// @param blsSigningKey is the signing key associated with the `blsPublicKey` and is used to locally sign a message never leaving the SDK
    formRegistrationSignature(blsPublicKey, payoutRecipient, proposerRegistryAddress, blsSigningKey) {
        return _formRegistrationSignature(blsPublicKey, payoutRecipient, proposerRegistryAddress, blsSigningKey)
    }

    /// @param signature the BLS signature over the SSZ validator registration container
    /// @param blsPublicKey The BLS public key registering in the PoN proposer registry
    /// @param payoutRecipient ETH ECDSA address that will be the recipient of any revenue associated with being registered in the proposer registry
    /// @param representative The manager of the BLS public key in the proposer registry
    swapSignature(signature, blsPublicKey, payoutRecipient, representative) {
        return _swapSignature(this.signer, signature, blsPublicKey, payoutRecipient, representative);
    }

    batchRegisterProposer(blsKeys, payoutRecipients, representatives, blsSigningKeys, openClaims) {
        return _batchRegisterProposer(this.signer, blsKeys, payoutRecipients, representatives, blsSigningKeys, openClaims);
    }

    /// @dev payoutRecipient Is the ETH address that is allowed to claim payouts from the payout pool
    registerProposer(blsKey, payoutRecipient, representative, blsSigningKey, openClaim = false) {
        return _registerProposer(this.signer, blsKey, payoutRecipient, representative, blsSigningKey, openClaim);
    }

    /// @dev alternativeFeeRecipient is the registered address that will keep the block rewards since it is not part of the payout pool
    /// @dev payoutRecipient Is the ETH address that is allowed to claim payouts from the payout pool if the proposer opts back in to the payout pool
    registerProposerWithoutPayoutPoolRegistration(blsKey, alternativeFeeRecipient, payoutRecipient, representative, blsSigningKey, openClaim = false) {
        return _registerProposerWithoutPayoutPoolRegistration(this.signer, blsKey, alternativeFeeRecipient, payoutRecipient, representative, blsSigningKey, openClaim);
    }

    registerProposerWithSignature(blsPublicKey, payoutRecipient, registrationSignature, ecdsaSig, openClaim) {
        return _registerProposerWithSignature(this.signer, blsPublicKey, payoutRecipient, registrationSignature, ecdsaSig, openClaim);
    }

    async registerProposerOutsidePayoutPoolUsingSignature(blsPublicKey, alternativeFeeRecipient, payoutRecipient, registrationSignature, ecdsaSig, openClaim) {
        const registry = await _getRegistry(this.signer);
        return registry.registerProposerWithoutPayoutPoolRegistration(
            blsPublicKey,
            payoutRecipient,
            registrationSignature,
            ecdsaSig,
            openClaim,
            alternativeFeeRecipient
        );
    }

    batchRegisterProposerWithoutPayoutPoolRegistration(blsKeys, alternativeFeeRecipients, payoutRecipients, representatives, blsSigningKeys, openClaims) {
        return _batchRegisterProposerWithoutPayoutPoolRegistration(this.signer, blsKeys, alternativeFeeRecipients, payoutRecipients, representatives, blsSigningKeys, openClaims);
    }

    activateProposer() {
        return _activateProposer(this.signer);
    }

    getProposer(blsPublicKey) {
        return _getProposer(this.signer, blsPublicKey);
    }

    positionProposerForRagequit(blsPublicKey) {
        return _positionProposerForRagequit(this.signer, blsPublicKey);
    }

    ragequitProposer(blsPublicKey) {
        return _ragequitProposer(this.signer, blsPublicKey);
    }
}

module.exports = ProposerRegistry
